import React, { useState } from 'react';
import { GroupChatModel } from '../../types/group';
import Modal from '../../Common/Components/Modal';

interface DeleteGroupDialogProps {
  group: GroupChatModel;
  onClose: () => void;
  onDelete: () => Promise<void>;
  show: boolean;
}

const DeleteGroupDialog: React.FC<DeleteGroupDialogProps> = ({
  group,
  onClose,
  onDelete,
  show,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onDelete();
      onClose();
    } catch (error) {
      console.error('Error deleting group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onClose} id="deleteGroupModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Delete Group</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="text-center">
          <h5 className="mb-3 text-16">Confirm Deletion</h5>
          <p className="text-slate-500 dark:text-zink-200 mb-2">
            Are you sure you want to delete the group <strong>{group.name}</strong>?
          </p>
          <p className="text-slate-500 dark:text-zink-200">
            ⚠️ This action cannot be undone. All messages and data associated with this group will be permanently deleted.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={onClose}
          className="px-4 py-2 text-slate-600 dark:text-zink-300 border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          onClick={handleDelete}
          disabled={isLoading}
          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          Delete Group
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteGroupDialog;